import moment from "moment";

// Who actually performed the triggering action — a member acting on their own behalf through
// the User App, vs. a coach/admin acting through the Office App — as opposed to which app is
// currently displaying the notification (both apps show both sources). Backed by the existing
// `created_by_type` column the API already returns (see api/src/services/notification.service.ts);
// "system"/null (no human actor, e.g. an automated job) falls back to "office-app" since those
// are operationally staff-facing, not member-initiated.
function sourceFromCreator(n) {
  return n.created_by_type === "client" ? "user-app" : "office-app";
}

// Maps a raw API notification (type + whatever entity data GET /notifications joined in) to
// the {id, type, source, message, timestamp, read, route} shape NotificationsDrawer.jsx already
// renders — the API deliberately stores no pre-rendered text/route (see the notifications
// migration), so each app decides its own copy/routing here, per `type`.
export function toDisplayNotification(n) {
  const clientName =
    `${n.client_first_name || ""} ${n.client_last_name || ""}`.trim() || "Klijent";
  const timeslotWhen = n.timeslot_start
    ? moment(n.timeslot_start).format("dddd, DD.MM.YYYY [u] HH:mm")
    : "";

  const base = {
    id: n.id,
    timestamp: n.created_at,
    read: n.read,
    source: sourceFromCreator(n),
  };

  switch (n.type) {
    case "client_registered":
      return { ...base, type: "register", message: `${clientName} se registrovao/la.`, route: "/members" };
    case "client_verified_email":
      return { ...base, type: "verified", message: `${clientName} je verifikovao/la email.`, route: "/members" };
    case "client_verified_phone":
      return { ...base, type: "verified", message: `${clientName} je verifikovao/la broj telefona.`, route: "/members" };
    case "profile_updated":
      return { ...base, type: "profile", message: `${clientName} je ažurirao/la svoj profil.`, route: "/members" };
    case "booking_created":
      return {
        ...base,
        type: "booking",
        message: `${clientName} je zakazao/la termin za ${timeslotWhen}.`,
        route: "/",
      };
    case "booking_canceled":
      return {
        ...base,
        type: "cancel",
        message: `${clientName} je otkazao/la termin za ${timeslotWhen}.`,
        route: "/",
      };
    case "package_assigned":
      return {
        ...base,
        type: "package",
        message: `${clientName} je dobio/la paket "${n.package_name || ""}".`,
        route: "/members",
      };
    case "package_updated":
      return {
        ...base,
        type: "package",
        message: `Paket klijenta ${clientName} je izmenjen.`,
        route: "/members",
      };
    default:
      return { ...base, type: "profile", message: `Novo obaveštenje za ${clientName}.`, route: "/members" };
  }
}

// "pre 2 min" / "pre 3 sata" / "pre 2 dana" / falls back to a plain date past a week.
export function formatRelativeTime(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "upravo sada";
  if (diffMin < 60) return `pre ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `pre ${diffH} ${diffH === 1 ? "sat" : diffH < 5 ? "sata" : "sati"}`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `pre ${diffD} ${diffD === 1 ? "dan" : "dana"}`;
  const d = new Date(iso);
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}.`;
}
